// Stock returns after past earnings reports, from the earnings calendar and the
// daily candles the stock page already loads (both from the same API calls as
// the price chart).
//
// Each return is close-to-close over N trading days from a base close:
//   - bmo (before open) and dmh (during market hours): the close before the
//     report date, since the reaction happens on the report day;
//   - amc (after close): the report-day close (the prior close when the report
//     date is not a trading day);
//   - unknown time: the close before the report date, so the reaction is
//     inside the window whichever session it really was. Such rows are flagged.

export const HORIZONS = [1, 5, 21];

function dayKey(v) {
  return String(v || "").slice(0, 10);
}

// First index whose date is >= key (dates ascending), or dates.length.
function lowerBound(dates, key) {
  let lo = 0;
  let hi = dates.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (dates[mid] < key) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Past reports, newest first:
 * [{ date, hour, hourKnown, returns: [pct | null per horizon] }].
 * Reports after the latest candle are left out; returns whose window runs past
 * it are null (they fill in as new closes arrive).
 */
export function earningsReturns(earnings, candles, horizons = HORIZONS) {
  const list = (Array.isArray(candles) ? candles : []).filter((c) => c && c.timeStamp && Number(c.close) > 0);
  if (!list.length) return [];
  const dates = list.map((c) => dayKey(c.timeStamp));
  const closes = list.map((c) => Number(c.close));
  const lastDate = dates[dates.length - 1];

  const seen = new Set();
  const rows = [];
  (Array.isArray(earnings) ? earnings : []).forEach((e) => {
    const date = dayKey(e && e.date);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date > lastDate || seen.has(date)) return;
    seen.add(date);
    const hour = String(e.hour || "").toLowerCase();
    const i = lowerBound(dates, date);
    const base = hour === "amc" && dates[i] === date ? i : i - 1;
    if (base < 0) return; // before the candle history
    const returns = horizons.map((k) =>
      base + k < closes.length ? (closes[base + k] / closes[base] - 1) * 100 : null
    );
    rows.push({ date, hour, hourKnown: ["amc", "bmo", "dmh"].includes(hour), returns });
  });
  return rows.sort((a, b) => (a.date < b.date ? 1 : -1));
}

function median(values) {
  if (!values.length) return null;
  const s = values.slice().sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/**
 * Per-horizon summary over rows: { positive, negative, medianPositive,
 * medianNegative, maxPositive, maxNegative, count }. Missing values are skipped;
 * a flat 0.0% counts as neither positive nor negative.
 */
export function earningsSummary(rows, horizons = HORIZONS) {
  return horizons.map((_, h) => {
    const values = rows.map((r) => r.returns[h]).filter((v) => v !== null && isFinite(v));
    const pos = values.filter((v) => v > 0);
    const neg = values.filter((v) => v < 0);
    return {
      count: values.length,
      positive: pos.length,
      negative: neg.length,
      medianPositive: median(pos),
      medianNegative: median(neg),
      maxPositive: pos.length ? Math.max(...pos) : null,
      maxNegative: neg.length ? Math.min(...neg) : null,
    };
  });
}

/**
 * Mean absolute 1-day move in percent and the number of reports it covers, or null.
 * Reports with an unknown time are left out: their window spans two sessions.
 */
export function averageMove(rows, horizonIndex = 0) {
  const values = rows
    .filter((r) => r.hourKnown)
    .map((r) => r.returns[horizonIndex])
    .filter((v) => v !== null && isFinite(v));
  if (!values.length) return null;
  return { pct: values.reduce((s, v) => s + Math.abs(v), 0) / values.length, count: values.length };
}
